/**
 * CanvasFrozenArtworkLayer — read-only overlay that paints the wallet's FROZEN
 * artworks at their canonical Grid placement inside the Territory Canvas, so the
 * owner can see them while arranging other modules. Click a piece to open the
 * right-side PlacementControls; while editing, that piece is rendered at the
 * LIVE (unsaved) placement and can be DRAGGED directly on the canvas.
 *
 * Shares placementEditStore with PlacementControls (same live edit state).
 */
import { useEffect, useRef } from "react";
import type { PixelContent } from "../../lib/artwork-api";
import type { PlacedFrozenArtwork } from "./useOwnedFrozenArtworks";
import { placementEditStore } from "../artwork/placementEditStore";
import { usePlacementEditState } from "../artwork/usePlacementEdit";

function FrozenCell({ content, width, height }: { content: PixelContent; width: number; height: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    const transparentIdx = content.palette.length;
    let cell = 0;
    const total = width * height;
    for (const [sym, count] of content.rle) {
      for (let k = 0; k < count && cell < total; k++, cell++) {
        if (sym !== transparentIdx && content.palette[sym]) {
          ctx.fillStyle = content.palette[sym];
          ctx.fillRect(cell % width, Math.floor(cell / width), 1, 1);
        }
      }
    }
  }, [content, width, height]);
  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      style={{ width: "100%", height: "100%", imageRendering: "pixelated", display: "block", pointerEvents: "none", position: "relative", zIndex: 1 }}
    />
  );
}

export function CanvasFrozenArtworkLayer({
  items, w2s, scale, onEdit,
}: {
  items: PlacedFrozenArtwork[];
  w2s: (x: number, y: number) => { x: number; y: number };
  scale: number;
  onEdit: (artworkId: string) => void;
}) {
  const edit = usePlacementEditState();
  const dragRef = useRef<{ x: number; y: number; ax: number; ay: number } | null>(null);
  if (items.length === 0) return null;

  return (
    <div data-testid="canvas-frozen-layer" className="pointer-events-none absolute inset-0">
      {items.map((a) => {
        const editingThis = edit && edit.artworkId === a.artwork_id;
        const ax = editingThis ? edit.ax : a.anchor_x;
        const ay = editingThis ? edit.ay : a.anchor_y;
        const rot = editingThis ? edit.rotDeg : a.rotation;
        const s = w2s(ax, ay);
        const w = a.width * scale, h = a.height * scale;

        if (editingThis) {
          const onDown = (e: React.PointerEvent) => {
            e.stopPropagation();
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            dragRef.current = { x: e.clientX, y: e.clientY, ax, ay };
          };
          const onMove = (e: React.PointerEvent) => {
            const d = dragRef.current;
            if (!d) return;
            const nx = Math.max(0, Math.min(1000 - a.width, Math.round(d.ax + (e.clientX - d.x) / scale)));
            const ny = Math.max(0, Math.min(1000 - a.height, Math.round(d.ay + (e.clientY - d.y) / scale)));
            placementEditStore.update({ ax: nx, ay: ny });
          };
          const onUp = () => { dragRef.current = null; };
          return (
            <div
              key={a.artwork_id}
              data-testid={`canvas-frozen-editing-${a.artwork_id}`}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              className="pointer-events-auto absolute cursor-move"
              style={{ left: s.x, top: s.y, width: Math.max(4, w), height: Math.max(4, h), transform: `rotate(${rot}deg)`, transformOrigin: "center center", touchAction: "none" }}
            >
              {edit.bg && <span aria-hidden className="absolute inset-0" style={{ backgroundColor: edit.bg, opacity: edit.opacityPct / 100 }} />}
              <FrozenCell content={a.content} width={a.width} height={a.height} />
              <span aria-hidden className={`absolute inset-0 rounded-[2px] ring-2 ${edit.valid ? "ring-emerald-400" : "ring-red-400"}`} style={{ borderStyle: "dashed" }} />
            </div>
          );
        }

        return (
          <button
            key={a.artwork_id}
            type="button"
            data-testid={`canvas-frozen-artwork-${a.artwork_id}`}
            onClick={(e) => { e.stopPropagation(); onEdit(a.artwork_id); }}
            title={`${a.title} — frozen · click to edit placement`}
            className="group pointer-events-auto absolute cursor-pointer border-0 bg-transparent p-0"
            style={{ left: s.x, top: s.y, width: Math.max(4, w), height: Math.max(4, h), transform: `rotate(${rot}deg)`, transformOrigin: "center center" }}
          >
            {a.background && <span aria-hidden className="absolute inset-0" style={{ backgroundColor: a.background, opacity: a.emptyOpacityBps / 10000 }} />}
            <FrozenCell content={a.content} width={a.width} height={a.height} />
            <span aria-hidden className="absolute inset-0 rounded-[2px] ring-1 ring-teal-300/50 group-hover:ring-2 group-hover:ring-teal-300" />
            <span
              className="absolute -top-5 left-0 whitespace-nowrap rounded-sm bg-teal-400/90 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-black opacity-0 group-hover:opacity-100"
              style={{ transform: `rotate(${-rot}deg)`, transformOrigin: "left top" }}
            >
              {a.title} · edit ✎
            </span>
          </button>
        );
      })}
    </div>
  );
}
